import { useCallback, useState } from 'react'

export interface ThreadSelection {
  selectMode: boolean
  picked: Set<string>
  toggleSelectMode: () => void
  togglePick: (id: string) => void
  selectAll: (visibleIds: string[]) => void
  clearSelection: () => void
  isPicked: (id: string) => boolean
}

export function useThreadSelection(): ThreadSelection {
  const [selectMode, setSelectMode] = useState(false)
  const [picked, setPicked] = useState<Set<string>>(new Set())

  const clearSelection = useCallback(() => {
    setPicked(new Set())
    setSelectMode(false)
  }, [])

  const togglePick = useCallback((id: string) => {
    setPicked((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
    setSelectMode(true)
  }, [])

  const toggleSelectMode = useCallback(() => {
    setSelectMode((m) => !m)
    setPicked(new Set())
  }, [])

  const selectAll = useCallback((visibleIds: string[]) => {
    setPicked((prev) => {
      const allSelected =
        visibleIds.length > 0 && visibleIds.every((id) => prev.has(id))
      return allSelected ? new Set() : new Set(visibleIds)
    })
    setSelectMode(true)
  }, [])

  const isPicked = useCallback((id: string) => picked.has(id), [picked])

  return {
    selectMode,
    picked,
    toggleSelectMode,
    togglePick,
    selectAll,
    clearSelection,
    isPicked,
  }
}
